import {Button, ButtonGroup, BlockStack, Text, Box} from '@shopify/polaris';
import {SmileyHappyIcon, SmileySadIcon} from '@shopify/polaris-icons';
import {useI18n} from '@shopify/react-i18n';
import {useUrl} from '@web-utilities/url';
import {useState, useCallback, useEffect} from 'react';
import {useLocalStorage} from '@web-utilities/local-storage';
import {FeedbackModal} from 'components/FeedbackModal';
import {useMedia} from '@shopify/react-hooks';

import {
  HIDE_FEEDBACK_CARD_STORAGE_KEY,
  SHOW_FEEDBACK_CARD_STORAGE_KEY,
  DISMISS_EXPIRES_AT_DAYS,
  FEEDBACK_EXPIRES_AT_DAYS,
  REVIEWED_EXPIRES_AT_YEARS,
} from './constants';
import {daysBetween} from './helper';
import styles from './FeedbackCard.scss';

export function FeedbackCard() {
  const isSmallScreen = useMedia('(max-width: 65em)');
  const [i18n] = useI18n();
  const urlHelper = useUrl();
  const [
    {
      value: hideFeedbackDateValue,
      update: storeHideFeedbackDateKey,
      remove: removeHideFeedbackDateKey,
    },
  ] = useLocalStorage<string>(HIDE_FEEDBACK_CARD_STORAGE_KEY);
  const [{value: showFeedbackDateValue}] = useLocalStorage<string>(
    SHOW_FEEDBACK_CARD_STORAGE_KEY,
  );
  const [dismissed, setDismissed] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const today = new Date();
  const showFeedback = showFeedbackDateValue
    ? daysBetween(today, new Date(showFeedbackDateValue)) >= 3
    : false;

  useEffect(() => {
    if (!hideFeedbackDateValue) return;

    const hideFeedbackDate = new Date(hideFeedbackDateValue);

    if (daysBetween(hideFeedbackDate, new Date()) < 0) {
      removeHideFeedbackDateKey();
    }
  }, [hideFeedbackDateValue, removeHideFeedbackDateKey]);

  const cardMarkup = dismissed ? null : (
    <div className={styles.FeedbackCard}>
      <Box
        padding="400"
        background="bg-surface-secondary"
        borderBlockStartWidth="025"
        borderBlockEndWidth="025"
        borderColor="border-secondary"
        position="relative"
        borderEndEndRadius={isSmallScreen ? '300' : undefined}
        borderEndStartRadius={isSmallScreen ? '300' : undefined}
      >
        <BlockStack gap="200">
          <Text as="h2" variant="headingMd" fontWeight="semibold">
            {i18n.translate('FeedbackCard.shareFeedback')}
          </Text>
          <p>{i18n.translate('FeedbackCard.shareFeedbackDescription')}</p>
          <div className={styles.FeedbackButtonGroup}>
            <ButtonGroup fullWidth>
              <Button
                icon={SmileyHappyIcon}
                variant="plain"
                onClick={() => {
                  setDismissed(true);
                  const expires = new Date();
                  expires.setFullYear(
                    expires.getFullYear() + REVIEWED_EXPIRES_AT_YEARS,
                  );
                  storeHideFeedbackDateKey(expires.toISOString());
                  window.open(
                    urlHelper.translateAndAdaptReviewUrl(i18n.locale),
                    '_blank',
                  );
                }}
              >
                {i18n.translate('FeedbackCard.goodFeedback')}
              </Button>
              <Button
                icon={SmileySadIcon}
                variant="plain"
                onClick={() => setFeedbackModalOpen(true)}
              >
                {i18n.translate('FeedbackCard.badFeedback')}
              </Button>
              <Button
                variant="monochromePlain"
                removeUnderline
                onClick={() => {
                  setDismissed(true);
                  const expires = new Date();
                  expires.setDate(expires.getDate() + DISMISS_EXPIRES_AT_DAYS);
                  storeHideFeedbackDateKey(expires.toISOString());
                }}
              >
                {i18n.translate('FeedbackCard.dismiss')}
              </Button>
            </ButtonGroup>
          </div>
        </BlockStack>
      </Box>
    </div>
  );

  const onSubmit = useCallback(() => {
    const expires = new Date();
    expires.setDate(expires.getDate() + FEEDBACK_EXPIRES_AT_DAYS);
    storeHideFeedbackDateKey(expires.toISOString());
    setFeedbackModalOpen(false);
  }, [storeHideFeedbackDateKey]);

  if (hideFeedbackDateValue || !showFeedback) {
    return null;
  }

  return (
    <>
      {cardMarkup}
      <FeedbackModal
        open={feedbackModalOpen}
        onSubmit={onSubmit}
        onCancel={() => setFeedbackModalOpen(false)}
      />
    </>
  );
}
